<template>
  <div class="firmware-edit pa-6 pa-md-10">
    <div class="edit-column">
      <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
        <v-btn v-if="currentManufacturerId" small text :to="{name: 'manufacturer', params: {id: currentManufacturerId}}">
          <v-icon small left>mdi-arrow-left</v-icon>{{ manufacturer ? manufacturer.name : $t('firmware.edit.back') }}
        </v-btn>
        <div class="text-h6 flex-grow-1">
          {{ firmware ? $t('firmware.edit.title', {version: firmware.name}) : $t('firmware.edit.new', {manufacturer: manufacturer ? manufacturer.name : ''}) }}
        </div>
        <template v-if="firmware">
          <v-chip :color="firmwareStatusColor(firmware.status)" text-color="white">{{ $t('firmware.firmware-statuses.' + firmware.status) }}</v-chip>
          <v-btn v-if="firmware.status !== 'draft'" small outlined @click="trialDialog = true">{{ $t('firmware.trial.see') }}</v-btn>
          <v-btn v-if="canDeployFirmware && !firmware.managed && ['testing', 'validated'].includes(firmware.status) && chargingPoints && chargingPoints.compatibleIds.length" small color="primary" @click="updateTo">
            {{ $tc('firmware.edit.update-to', chargingPoints.compatibleIds.length, {count: chargingPoints.compatibleIds.length}) }}
          </v-btn>
          <v-menu v-if="canEditCatalogue && moves.length" offset-y left>
            <template #activator="{on}">
              <v-btn small outlined v-on="on">{{ $t('firmware.edit.change-status') }}<v-icon small right>mdi-menu-down</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="move in moves" :key="move" :disabled="!!moveLock(move)" @click="askStatus(move)">
                <v-list-item-content>
                  <v-list-item-title>{{ $t('firmware.edit.moves.' + move) }}</v-list-item-title>
                  <v-list-item-subtitle v-if="moveLock(move)" class="text-wrap" style="max-width: 280px">{{ moveLock(move) }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </div>
      <div v-if="firmware && firmware.status !== 'draft'" class="text-caption text--secondary mb-2">
        {{ $t('firmware.edit.status-changed', {status: $t('firmware.firmware-statuses.' + firmware.status).toLowerCase(), date: formatDate(firmware.statusChangedAt, 'DD/MM/YYYY HH:mm'), by: firmware.statusChangedBy || '-'}) }}
      </div>
      <v-alert v-if="firmware && firmware.statusReason" type="info" text dense>{{ firmware.statusReason }}</v-alert>
      <v-alert v-if="firmware && !firmware.version && firmware.status === 'testing'" type="info" text dense>{{ $t('firmware.edit.version-pending') }}</v-alert>

      <div v-if="loading" class="text-center my-10"><v-progress-circular indeterminate color="primary" /></div>

      <v-form v-else ref="form" :disabled="!canEditCatalogue" @submit.prevent="save">
        <!-- 1. Le fichier -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">1. {{ $t('firmware.edit.file') }}</v-card-title>
          <v-card-text>
            <!-- Géré par le constructeur : la borne va chercher sa dernière version chez lui, il n'y a pas de fichier -->
            <div v-if="firmware && firmware.managed" class="text-body-2">{{ $t('firmware.edit.managed') }}</div>
            <div v-else-if="firmware && firmware.file" class="d-flex align-center flex-wrap" style="gap: 12px">
              <v-icon>mdi-file-outline</v-icon>
              <div class="flex-grow-1">
                <div class="font-weight-medium">{{ firmware.file.filename }}</div>
                <div class="text-caption text--secondary">{{ size(firmware.file.size) }} · SHA-256 <span class="mono">{{ firmware.file.sha256.slice(0, 16) }}…</span> · {{ formatDate(firmware.file.uploadedAt) }}</div>
              </div>
            </div>
            <template v-if="firmware && firmware.managed" />
            <template v-else-if="!published">
              <v-file-input v-model="form.file" :label="firmware && firmware.file ? $t('firmware.edit.replace-file') : $t('firmware.edit.choose-file')" outlined dense show-size prepend-icon="mdi-upload" class="mt-2" />
              <v-text-field v-model="form.sha256" :label="$t('firmware.edit.sha256')" :hint="$t('firmware.edit.sha256-hint')" persistent-hint outlined dense />
            </template>
            <div v-else class="text-caption text--secondary mt-2">{{ $t('firmware.edit.file-frozen') }}</div>
          </v-card-text>
        </v-card>

        <!-- 2. La version -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">2. {{ $t('firmware.edit.version') }}</v-card-title>
          <v-card-text>
            <v-combobox
                v-model="form.version"
                :items="seenVersions"
                :label="$t('firmware.edit.version-label')"
                :hint="$t('firmware.edit.version-hint')"
                :rules="[v => form.unknownVersion || !!(v && String(v).trim()) || $t('firmware.edit.version-required')]"
                :disabled="published || form.unknownVersion"
                persistent-hint
                outlined
                dense
                class="mono-input"
            />
            <v-checkbox v-model="form.unknownVersion" :disabled="published" :label="$t('firmware.edit.unknown-version')" :hint="$t('firmware.edit.unknown-version-hint')" persistent-hint class="mt-1" />
            <v-btn v-if="published && firmware.version && canEditCatalogue" small text color="primary" class="mt-2 px-0" @click="askRename(firmware.version)">
              <v-icon small left>mdi-pencil</v-icon>{{ $t('firmware.edit.rename') }}
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- 3. Pour quels modèles -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">3. {{ $t('firmware.edit.models') }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="form.allModels" row class="mt-0">
              <v-radio :value="true" :label="$t('firmware.edit.all-models', {manufacturer: manufacturer ? manufacturer.name : ''})" />
              <v-radio :value="false" :label="$t('firmware.edit.some-models')" />
            </v-radio-group>
            <div v-if="!form.allModels">
              <!-- Les gammes d'abord : leurs types suivent, même ceux qui n'existent pas encore sur le parc. -->
              <template v-if="productLines.length">
                <div class="text-caption text--secondary mb-1">{{ $t('firmware.edit.product-lines') }}</div>
                <v-row dense class="mb-2">
                  <v-col v-for="line in productLines" :key="line.id" cols="12" sm="6" md="4">
                    <v-checkbox v-model="form.productLineIds" :value="line.id" dense hide-details class="mt-0">
                      <template #label>{{ line.name }}&nbsp;<span class="text--secondary">({{ line.chargingPoints }})</span></template>
                    </v-checkbox>
                  </v-col>
                </v-row>
                <div class="text-caption text--secondary mb-1">{{ $t('firmware.edit.announced-models') }}</div>
              </template>
              <v-row dense>
                <v-col v-for="item in modelChoices" :key="item.model" cols="12" sm="6" md="4">
                  <v-checkbox v-if="lineOf(item)" :input-value="true" disabled dense hide-details class="mt-0">
                    <template #label><span class="mono">{{ item.model }}</span>&nbsp;<span class="text--secondary">({{ item.count }}) · {{ $t('firmware.edit.in-product-line', {name: lineOf(item).name}) }}</span></template>
                  </v-checkbox>
                  <v-checkbox v-else-if="item.coveredBy || item.excludedBy" :input-value="!!item.coveredBy" disabled dense hide-details class="mt-0">
                    <template #label><span class="mono">{{ item.model }}</span>&nbsp;<span class="text--secondary">({{ item.count }}) · {{ $t(item.coveredBy ? 'firmware.edit.covered-by' : 'firmware.edit.excluded-by', {pattern: item.coveredBy || item.excludedBy}) }}</span></template>
                  </v-checkbox>
                  <v-checkbox v-else v-model="form.models" :value="item.model" dense hide-details class="mt-0">
                    <template #label><span class="mono">{{ item.model }}</span>&nbsp;<span class="text--secondary">({{ item.count }})</span></template>
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-combobox
                  v-model="form.modelPatterns"
                  :label="$t('firmware.edit.model-patterns')"
                  :hint="$t('firmware.edit.model-patterns-hint')"
                  persistent-hint
                  multiple
                  small-chips
                  deletable-chips
                  outlined
                  dense
                  class="mt-3"
              />
            </div>
            <div v-if="preview" class="text-body-2 mt-2"><b>{{ $tc('firmware.edit.matched', preview.chargingPoints, {count: preview.chargingPoints}) }}</b></div>
          </v-card-text>
        </v-card>

        <!-- 4. Installable depuis -->
        <v-card outlined class="mb-4 from-card">
          <v-card-title class="text-subtitle-1">4. {{ $t('firmware.edit.from') }}</v-card-title>
          <v-card-text>
            <p class="text-body-2">{{ $t('firmware.edit.from-explained') }}</p>
            <v-radio-group v-model="form.fromAny" class="mt-0">
              <v-radio :value="true" :label="$t('firmware.edit.from-any')" />
              <v-radio :value="false" :label="$t('firmware.edit.from-only')" />
            </v-radio-group>
            <div v-if="!form.fromAny" class="ml-8">
              <div class="text-caption text--secondary mb-1">{{ $t('firmware.edit.from-versions') }}</div>
              <v-row dense>
                <v-col v-for="item in versionChoices" :key="item.version" cols="12" sm="6" md="4">
                  <v-checkbox v-if="item.coveredBy || item.excludedBy" :input-value="!!item.coveredBy" disabled dense hide-details class="mt-0">
                    <template #label><span class="mono">{{ item.version }}</span>&nbsp;<span class="text--secondary">({{ item.count }}) · {{ $t(item.coveredBy ? 'firmware.edit.covered-by' : 'firmware.edit.excluded-by', {pattern: item.coveredBy || item.excludedBy}) }}</span></template>
                  </v-checkbox>
                  <v-checkbox v-else v-model="form.installableFrom" :value="item.version" dense hide-details class="mt-0">
                    <template #label><span class="mono">{{ item.version }}</span>&nbsp;<span class="text--secondary">({{ item.count }})</span></template>
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-combobox
                  v-model="form.patterns"
                  :label="$t('firmware.edit.from-patterns')"
                  :hint="$t('firmware.edit.from-patterns-hint')"
                  persistent-hint
                  multiple
                  small-chips
                  deletable-chips
                  outlined
                  dense
                  class="mt-3"
              />
            </div>

            <v-sheet v-if="preview && preview.chargingPoints" color="grey lighten-4" rounded class="pa-3 mt-3">
              <div class="text-caption text--secondary mb-1">{{ $t('firmware.edit.outcome', {count: preview.chargingPoints}) }}</div>
              <div class="outcome"><span class="dot success" /><b>{{ preview.direct }}</b> {{ $t('firmware.edit.outcomes.direct') }}</div>
              <div v-for="via in preview.via" :key="via.steps.join()" class="outcome">
                <span class="dot primary" /><b>{{ via.count }}</b> {{ $t('firmware.edit.outcomes.via', {steps: via.steps.join(' → '), from: via.from.join(', ')}) }}
              </div>
              <div class="outcome"><span class="dot grey" /><b>{{ preview.already }}</b> {{ $t('firmware.edit.outcomes.already') }}</div>
              <div v-if="noPathCount" class="outcome">
                <span class="dot error" /><b>{{ noPathCount }}</b> {{ $t('firmware.edit.outcomes.no-path', {versions: noPathVersions}) }}
              </div>
            </v-sheet>
          </v-card-text>
        </v-card>

        <v-expansion-panels flat class="mb-4">
          <v-expansion-panel class="outlined-panel">
            <v-expansion-panel-header class="font-weight-medium">{{ $t('firmware.edit.more') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="font-weight-medium mb-1">{{ $t('firmware.edit.keys') }}</div>
              <div class="text-caption text--secondary mb-2">{{ $t('firmware.edit.keys-hint') }}</div>
              <div v-for="(key, index) in form.keys" :key="index" class="d-flex align-center flex-wrap" style="gap: 8px">
                <v-text-field v-model="key.key" :label="$t('firmware.edit.key-name')" :disabled="published" outlined dense hide-details style="flex: 1 1 200px" />
                <v-text-field v-model="key.value" :label="key.secret && key.saved ? $t('firmware.edit.key-secret-kept') : $t('firmware.edit.key-value')" :type="key.secret ? 'password' : 'text'" :disabled="published" outlined dense hide-details style="flex: 2 1 260px" />
                <v-checkbox v-model="key.secret" :label="$t('firmware.edit.key-secret')" :disabled="published" dense hide-details class="mt-0" />
                <v-btn v-if="!published" icon small :aria-label="$t('delete')" @click="form.keys.splice(index, 1)"><v-icon small>mdi-close</v-icon></v-btn>
              </div>
              <v-btn v-if="!published" small text class="mt-2" @click="form.keys.push({key: '', value: '', secret: false, saved: false})"><v-icon small left>mdi-plus</v-icon>{{ $t('firmware.edit.add-key') }}</v-btn>
              <div class="text-caption text--secondary mt-4">{{ $t('firmware.edit.protocol-from-manufacturer') }}</div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="canEditCatalogue" class="d-flex justify-end flex-wrap" style="gap: 12px">
          <v-btn v-if="firmware && firmware.status === 'draft'" outlined color="error" :loading="deleting" @click="remove">{{ $t('firmware.edit.delete') }}</v-btn>
          <v-btn color="primary" type="submit" :loading="saving">{{ $t('firmware.edit.save') }}</v-btn>
        </div>
      </v-form>

      <!-- Les tests : une borne qui a installé ce firmware, par son identifiant ; l'historique de ses fiches -->
      <!-- Les bornes qui l'ont installé : les pilotes en essai, puis tout le parc qui l'a -->
      <v-card v-if="published && !loading && chargingPoints" outlined class="mt-6">
        <v-card-title class="text-subtitle-1">{{ $tc('firmware.edit.installed-on', chargingPoints.installed, {count: chargingPoints.installed}) }}</v-card-title>
        <v-card-text>
          <div v-if="!chargingPoints.installed" class="text--secondary">{{ $t('firmware.edit.installed-none') }}</div>
          <div v-else class="d-flex flex-wrap" style="gap: 6px">
            <v-chip v-for="chargingPoint in chargingPoints.chargingPoints.slice(0, installedShown)" :key="chargingPoint.chargingPointId" small outlined :to="{name: 'charging-points.detail.manage', params: {id: chargingPoint.chargingPointId}}">
              <v-icon x-small left :color="chargingPoint.connected ? 'success' : 'grey'">mdi-circle</v-icon>{{ chargingPoint.name }}<span v-if="chargingPoint.locationName" class="text--secondary ml-1">· {{ chargingPoint.locationName }}</span>
            </v-chip>
            <v-btn v-if="chargingPoints.chargingPoints.length > installedShown" x-small text @click="installedShown += 50">{{ $t('firmware.edit.show-more') }}</v-btn>
          </div>
        </v-card-text>
      </v-card>

      <firmware-test-sheets v-if="published && !loading" :firmware="firmware" :charging-points="chargingPoints ? chargingPoints.chargingPoints : []" class="mt-6" />
    </div>

    <v-dialog v-model="statusDialog.open" max-width="520">
      <v-card v-if="statusDialog.open">
        <v-card-title>{{ $t('firmware.edit.moves.' + statusDialog.status) }}</v-card-title>
        <v-card-text>
          <p class="text-body-2">{{ $t('firmware.edit.move-explained.' + statusDialog.status) }}</p>
          <!-- Valider : l'installation a réussi, mais une version se prouve dans le temps — l'épreuve le dit. -->
          <v-alert v-if="statusDialog.status === 'validated' && (!firmware.trial || firmware.trial.proposal !== 'validate')" type="warning" text dense>
            {{ firmware.trial ? $t('firmware.trial.proposals.' + firmware.trial.proposal) : '' }} {{ $t('firmware.edit.validate-early') }}
          </v-alert>
          <!-- Firmware créé sous la règle : une fiche de test verte par modèle concerné ; qui peut forcer passe outre, avec un motif. -->
          <template v-if="statusDialog.status === 'validated' && !firmware.testsProven">
            <v-alert type="error" text dense>{{ testsMissing }}</v-alert>
            <v-checkbox v-model="statusDialog.withoutTests" :label="$t('firmware.edit.validate-without-tests')" dense hide-details class="mt-0 mb-3" />
          </template>
          <v-textarea v-model="statusDialog.reason" :label="needsReason ? $t('firmware.edit.reason-required') : $t('firmware.edit.reason-optional')" rows="2" auto-grow outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="statusDialog.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :loading="statusDialog.saving" :disabled="(needsReason && !statusDialog.reason.trim()) || (statusDialog.status === 'validated' && !firmware.testsProven && !statusDialog.withoutTests)" @click="changeStatus">{{ $t('firmware.edit.moves.' + statusDialog.status) }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Corriger la version annoncée d'un firmware publié, même installé : la borne fait foi. -->
    <v-dialog v-model="renameDialog.open" max-width="520">
      <v-card v-if="renameDialog.open">
        <v-card-title>{{ $t('firmware.edit.rename') }}</v-card-title>
        <v-card-text>
          <p class="text-body-2">{{ $t('firmware.edit.rename-explained', {version: firmware.version}) }}</p>
          <v-text-field v-model="renameDialog.version" :label="$t('firmware.edit.version-label')" outlined dense class="mono-input" />
          <v-textarea v-model="renameDialog.reason" :label="$t('firmware.edit.reason-required')" rows="2" auto-grow outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="renameDialog.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :loading="renameDialog.saving" :disabled="!renameDialog.version.trim() || !renameDialog.reason.trim()" @click="rename">{{ $t('firmware.edit.rename') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <firmware-trial-dialog v-if="firmware" v-model="trialDialog" :firmware="firmware" :can-edit="canEditCatalogue" :validate-lock="moveLock('validated')" :block-lock="moveLock('blocked')" @move="askStatus" />
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import ManufacturerRepository from "@repository/ManufacturerRepository";
import ProductLineRepository from "@repository/ProductLineRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";
import FirmwareTrialDialog from "./FirmwareTrialDialog.vue";
import FirmwareTestSheets from "./FirmwareTestSheets.vue";

/**
 * Motif ou valeur exacte, pour ranger une liste enregistrée entre les cases et le champ des motifs (même règle que
 * `Wildcard::isPattern` côté serveur ; ce que chaque motif prend, c'est le serveur qui le dit, dans l'aperçu).
 */
const isPattern = value => /^\s*!|[*?]|\[\d+\s*(-|\.\.)\s*\d*\]|\[\d*\s*(-|\.\.)\s*\d+\]/.test(value)

/**
 * Un firmware de son constructeur : son fichier, la version que la borne annonce (ou « à relever » à la première
 * installation), les modèles qui l'acceptent, et les versions depuis lesquelles il s'installe. L'aperçu se recalcule sur
 * le vrai parc du constructeur à chaque saisie. Le protocole de téléchargement vient des réglages du constructeur.
 */
export default {
  components: {FirmwareTrialDialog, FirmwareTestSheets},
  mixins: [FirmwareMixin],

  props: {
    // Un firmware existant…
    id: {type: String, default: null},
    // …ou un nouveau, dans ce constructeur.
    manufacturerId: {type: String, default: null},
  },

  data() {
    return {
      firmware: null,
      manufacturer: null,
      // Les gammes du constructeur, à viser en plus des modèles annoncés.
      productLines: [],
      loading: false,
      saving: false,
      deleting: false,
      form: this.emptyForm(),
      preview: null,
      previewing: false,
      previewTimer: null,
      trialDialog: false,
      chargingPoints: null,
      installedShown: 50,
      statusDialog: {open: false, status: null, reason: '', withoutTests: false, saving: false},
      renameDialog: {open: false, version: '', reason: '', saving: false},
      renameProposed: false,
    }
  },

  computed: {
    currentManufacturerId() {
      return this.firmware ? this.firmware.manufacturerId : this.manufacturerId
    },
    published() {
      return !!this.firmware && this.firmware.status !== 'draft'
    },
    // Les changements de statut permis, à la main : le serveur les donne, avec ce qui ferme le retour au brouillon.
    moves() {
      return this.firmware ? this.firmware.moves || [] : []
    },
    needsReason() {
      return this.statusDialog.status === 'blocked' || this.firmware?.status === 'blocked' || this.statusDialog.withoutTests
    },
    // Les modèles (ou gammes) qui n'ont pas encore leur fiche de test verte, en clair.
    testsMissing() {
      const untested = this.firmware?.untested || []
      return untested.length
        ? this.$t('firmware.edit.validate-needs-tests', {models: untested.join(', ')})
        : this.$t('firmware.edit.validate-needs-a-test')
    },
    // Les versions sans chemin, triées, la plus répandue d'abord ; celles sans version annoncée sont dites telles.
    noPathVersions() {
      return [...this.preview.noPath].sort((a, b) => (b.count || 0) - (a.count || 0))
          .map(row => row.version ? `${row.version} (${row.count})` : `${this.$t('firmware.launch.no-version')} (${row.count})`).join(', ')
    },
    seenVersions() {
      return (this.preview?.versions || []).map(v => v.version)
    },
    // Tous les modèles du constructeur sur le parc, et ceux déjà cochés qui n'y sont plus.
    modelChoices() {
      const seen = (this.preview?.models || []).filter(m => m.model)
      const extra = this.form.models.filter(model => !seen.some(m => m.model === model)).map(model => ({model, count: 0}))
      return [...seen, ...extra]
    },
    // Les versions vues sur les bornes visées, et celles déjà cochées qui n'y sont plus.
    versionChoices() {
      const seen = this.preview?.versions || []
      const extra = this.form.installableFrom.filter(v => !seen.some(s => s.version === v)).map(version => ({version, count: 0}))
      return [...seen, ...extra]
    },
    noPathCount() {
      return (this.preview?.noPath || []).reduce((sum, row) => sum + row.count, 0)
    },
    version() {
      return this.form.unknownVersion ? null : (String(this.form.version || '').trim() || null)
    },
    // Ce que valent « modèles » et « installable depuis » pour le serveur : vide = tous.
    models() {
      return this.form.allModels ? [] : [...new Set([...this.form.models, ...this.form.modelPatterns])]
    },
    productLineIds() {
      return this.form.allModels ? [] : this.form.productLineIds
    },
    installableFrom() {
      return this.form.fromAny ? [] : [...new Set([...this.form.installableFrom, ...this.form.patterns])]
    },
    previewKey() {
      return JSON.stringify([this.currentManufacturerId, this.version, this.models, this.productLineIds, this.installableFrom])
    },
  },

  watch: {
    previewKey() {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(this.loadPreview, 400)
    },
    id() {
      this.load()
    },
    currentManufacturerId: {
      immediate: true,
      handler(id) {
        if (id && this.manufacturer?.id !== id) {
          ManufacturerRepository.get(id).then(manufacturer => {
            this.manufacturer = manufacturer
          }).catch(err => this.notifyError(err))
          ProductLineRepository.ofManufacturer(id).then(data => {
            this.productLines = data.lines
          }).catch(err => this.notifyError(err))
        }
      },
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    emptyForm() {
      // Créer depuis une version annoncée par le parc : la fiche du constructeur passe la version et ses modèles.
      const query = this.$route.query
      const models = query.models ? [].concat(query.models) : []
      return {
        file: null,
        sha256: '',
        version: query.version || '',
        unknownVersion: false,
        allModels: models.length === 0,
        models,
        modelPatterns: [],
        productLineIds: [],
        fromAny: true,
        installableFrom: [],
        patterns: [],
        keys: [],
      }
    },

    load() {
      if (!this.id) {
        this.firmware = null
        this.form = this.emptyForm()
        this.loadPreview()
        return
      }
      this.loading = true
      FirmwareRepository.firmware(this.id).then(firmware => {
        this.firmware = firmware
        this.loadChargingPoints()
        // Venu d'une mise à jour échouée en « mauvaise version » : proposer la version que la borne annonce. Même déjà
        // corrigée, la valider reprend les mises à jour échouées.
        const proposed = this.$route.query.rename
        if (proposed && !this.renameProposed && firmware.status !== 'draft' && firmware.version && this.canEditCatalogue) {
          this.renameProposed = true
          this.askRename(proposed, this.$t('firmware.edit.rename-proposed-reason', {version: proposed}))
        }
        this.form = {
          ...this.emptyForm(),
          version: firmware.version || '',
          unknownVersion: !firmware.version,
          allModels: firmware.models.length === 0 && (firmware.productLineIds || []).length === 0,
          productLineIds: firmware.productLineIds || [],
          models: firmware.models.filter(m => !isPattern(m)),
          modelPatterns: firmware.models.filter(isPattern),
          fromAny: firmware.installableFrom.length === 0,
          installableFrom: firmware.installableFrom.filter(v => !isPattern(v)),
          patterns: firmware.installableFrom.filter(isPattern),
          keys: firmware.configurationKeys.map(key => ({key: key.key, value: key.value || '', secret: key.secret, saved: true})),
        }
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    // La gamme cochée qui prend ce modèle annoncé (il suit alors la gamme).
    lineOf(item) {
      return item.productLineId && this.form.productLineIds.includes(item.productLineId)
        ? this.productLines.find(line => line.id === item.productLineId) || null
        : null
    },

    loadPreview() {
      if (!this.currentManufacturerId) {
        return
      }
      this.previewing = true
      FirmwareRepository.preview({
        manufacturerId: this.currentManufacturerId,
        version: this.version,
        models: this.models,
        productLineIds: this.productLineIds,
        installableFrom: this.installableFrom,
        firmwareId: this.firmware?.id || null,
      }).then(preview => {
        this.preview = preview
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.previewing = false
      })
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.saving = true
      try {
        const {id} = await FirmwareRepository.saveFirmware({
          id: this.firmware?.id,
          manufacturerId: this.currentManufacturerId,
          version: this.version,
          models: this.models,
          productLineIds: this.productLineIds,
          installableFrom: this.installableFrom,
          configurationKeys: this.published ? null : this.form.keys
            .filter(key => key.key.trim())
            .map(key => ({key: key.key.trim(), value: key.value || null, secret: key.secret})),
        })
        // Créé : on passe aussitôt sur sa fiche, pour qu'un échec de l'envoi du fichier ne fasse pas créer un second
        // brouillon au clic suivant.
        const created = this.firmware?.id !== id
        if (created) {
          await this.$router.replace({name: 'firmware.edit', params: {id}})
        }
        if (this.form.file) {
          await FirmwareRepository.uploadFile(id, this.form.file, this.form.sha256.trim() || null)
        }
        this.notifySuccess('firmware.edit.saved')
        if (!created) {
          this.load()
        }
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.saving = false
      }
    },

    remove() {
      this.$dialog.confirm({text: this.$t('firmware.edit.delete-confirm', {version: this.firmware.name})}).then(result => {
        if (result) {
          this.doRemove()
        }
      })
    },

    // Les bornes visées : celles de ses modèles qui ne l'ont pas ; on choisit ensuite le rythme et la fenêtre.
    updateTo() {
      this.$router.push({name: 'firmware.launch', params: {chargingPointIds: this.chargingPoints.compatibleIds, targetVersionId: this.firmware.id}})
    },

    loadChargingPoints() {
      if (!this.firmware || this.firmware.status === 'draft') {
        return
      }
      FirmwareRepository.firmwareChargingPoints(this.firmware.id).then(chargingPoints => {
        this.chargingPoints = chargingPoints
      }).catch(() => {})
    },

    doRemove() {
      this.deleting = true
      FirmwareRepository.deleteFirmware(this.firmware.id).then(() => {
        this.notifySuccess('firmware.edit.deleted')
        this.$router.push({name: 'manufacturer', params: {id: this.currentManufacturerId}})
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.deleting = false
      })
    },

    askRename(version, reason = '') {
      this.renameDialog = {open: true, version: version || '', reason, saving: false}
    },

    rename() {
      this.renameDialog.saving = true
      FirmwareRepository.renameVersion(this.firmware.id, this.renameDialog.version.trim(), this.renameDialog.reason.trim()).then(() => {
        this.renameDialog.open = false
        this.notifySuccess('firmware.edit.renamed')
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.renameDialog.saving = false
      })
    },

    // Pourquoi un changement de statut est fermé (grisé, avec la raison), ou null.
    moveLock(move) {
      if (move === 'draft' && this.firmware.draftLock) {
        return this.$t('firmware.edit.draft-locked.' + this.firmware.draftLock)
      }
      if (move === 'validated' && this.firmware.status === 'testing' && !this.firmware.installed) {
        return this.$t('firmware.edit.validate-needs-install')
      }
      // Sans les fiches de test : fermé, sauf à qui peut forcer (la fenêtre le lui dit et demande un motif).
      if (move === 'validated' && this.firmware.status === 'testing' && !this.firmware.testsProven && !this.canForceFirmware) {
        return this.testsMissing
      }
      return null
    },

    askStatus(status) {
      this.trialDialog = false
      this.statusDialog = {open: true, status, reason: '', withoutTests: false, saving: false}
    },

    changeStatus() {
      this.statusDialog.saving = true
      FirmwareRepository.changeStatus(this.firmware.id, this.statusDialog.status, this.statusDialog.reason.trim() || null, this.statusDialog.withoutTests).then(() => {
        this.statusDialog.open = false
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.statusDialog.saving = false
      })
    },

    size(bytes) {
      return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} Ko` : `${(bytes / 1024 / 1024).toFixed(1)} Mo`
    },
  },
}
</script>

<style scoped lang="scss">
.firmware-edit {
  .edit-column {
    max-width: 960px;
    margin: 0 auto;
  }
  .mono, .mono-input input {
    font-family: ui-monospace, monospace;
  }
  .from-card {
    border: 2px solid var(--v-primary-base) !important;
  }
  .outlined-panel {
    border: thin solid rgba(0, 0, 0, 0.12);
  }
  .outcome {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    margin: 2px 0;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex: 0 0 auto;
  }
}
</style>
